import { useMemo } from 'react';
import { Capacitor } from '@capacitor/core';

export interface PlatformContext {
  isIosApp: boolean;
  isAndroidApp: boolean;
  isWebBrowser: boolean;
}

const SOURCE_STORAGE_KEY = 'sf_app_source';
const VALID_SOURCES = new Set(['ios_app', 'android_app']);

// Reads `?source=` from the URL on first hit and persists it to sessionStorage
// so it survives client-side navigation inside the in-app WebView. Without this,
// the moment React Router swaps the URL the source param vanishes and the next
// hook caller would mis-classify the in-app WebView as a plain web browser.
const readSourceParam = (): string | null => {
  if (typeof window === 'undefined') return null;

  let urlSource: string | null = null;
  try {
    urlSource = new URLSearchParams(window.location.search).get('source');
  } catch {
    urlSource = null;
  }

  if (urlSource && VALID_SOURCES.has(urlSource)) {
    try { sessionStorage.setItem(SOURCE_STORAGE_KEY, urlSource); } catch {}
    return urlSource;
  }

  try {
    return sessionStorage.getItem(SOURCE_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const usePlatformContext = (): PlatformContext => {
  return useMemo(() => {
    const platform = Capacitor.getPlatform();
    const native = Capacitor.isNativePlatform();
    const source = readSourceParam();

    // Strictest "this is app context" wins. Capacitor reports the native
    // platform when injected; the source query param covers SFSafariViewController
    // and Android Custom Tabs where Capacitor isn't injected.
    const isIosApp = (native && platform === 'ios') || source === 'ios_app';
    const isAndroidApp = (native && platform === 'android') || source === 'android_app';
    const isWebBrowser = !isIosApp && !isAndroidApp;

    return { isIosApp, isAndroidApp, isWebBrowser };
  }, []);
};
